import type { Section, Task } from '../db/models'

export interface SectionReorderUpdate { table: 'section' | 'task'; id: string; order: number }

/**
 * **Abschnitte umsortieren** (Desktop: `useSectionDrag`, Handy: Sheet „Abschnitte ordnen“). Alles im Projekt wird in
 * der neuen Folge durchgezählt: erst die Aufgaben ohne Abschnitt, dann je Abschnitt der Kopf und seine Aufgaben —
 * auch erledigte und eingeklappte. Eine Aufgabe gehört zum Kopf über ihr; so wandert sie mit, ohne dass sich ihre
 * `sectionId` ändert. Zurück kommt nur, was sich ändert. Abschnitte, die `sectionIds` nicht nennt, bleiben, wo sie
 * sind (der Desktop nennt nur die sichtbaren). Liegt in `lib/`, damit beide dieselbe Regel teilen (ADR 0036).
 */
export function planSectionReorder(tasks: readonly Task[], sections: readonly Section[], sectionIds: readonly string[]): SectionReorderUpdate[] {
  type OrderItem = { type: 'task'; data: Task } | { type: 'section'; data: Section }
  const allItems: OrderItem[] = [
    ...tasks.map(t => ({ type: 'task' as const, data: t })),
    ...sections.map(s => ({ type: 'section' as const, data: s })),
  ]
  allItems.sort((a, b) => a.data.order - b.data.order)

  const fullTaskMap = new Map<string | null, Task[]>()
  let curSectionId: string | null = null
  for (const item of allItems) {
    if (item.type === 'section') {
      curSectionId = item.data.id
      if (!fullTaskMap.has(curSectionId)) fullTaskMap.set(curSectionId, [])
    } else {
      if (!fullTaskMap.has(curSectionId)) fullTaskMap.set(curSectionId, [])
      fullTaskMap.get(curSectionId)!.push(item.data)
    }
  }

  const byId = new Map(sections.map(s => [s.id, s]))
  const updates: SectionReorderUpdate[] = []
  let order = 0
  for (const t of fullTaskMap.get(null) ?? []) {
    order++
    if (t.order !== order) updates.push({ table: 'task', id: t.id, order })
  }
  for (const sectionId of sectionIds) {
    const section = byId.get(sectionId)
    if (!section) continue
    order++
    if (section.order !== order) updates.push({ table: 'section', id: sectionId, order })
    for (const t of fullTaskMap.get(sectionId) ?? []) {
      order++
      if (t.order !== order) updates.push({ table: 'task', id: t.id, order })
    }
  }
  return updates
}
